import React, {
  ReactElement,
  ReactNode,
  useRef,
} from 'react';
import clsx from 'clsx';
import UploadIcon from 'core/icons/upload';
import {
  FileLocalUrl,
} from 'pages/Dashboard/components/FilePicker';
import Button from 'pages/Dashboard/components/Button';
import {
  mediaEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import FileInput from 'pages/Dashboard/components/FilePicker/FileInput';
import {
  WebcamScanDialogProps,
} from 'views/EMR/components/WebcamScanDialog';
import {
  useMixpanelTrack,
} from 'core/hooks';
import upperFirst from 'lodash/upperFirst';
import camelCase from 'lodash/camelCase';
import ActionMenu, {
  DropdownMenuItem,
} from 'core/components/DropdownMenu';
import SplitActionButton from 'core/components/SplitActionButton';

type ExtraMenuItem = {
  id: string;
  label: string;
  onClick: () => void;
};

type Parts = 'root' | 'primary' | 'caret' | 'popup' | 'trigger';
type TriggerMode = 'split' | 'menu';

type Props = {
  onUpload: (files: FileLocalUrl[]) => Promise<void> | void;
  allowedFileTypes?: string;
  isUploading?: boolean;
  scanProps?: WebcamScanDialogProps;
  label?: string;
  multiple?: boolean;
  buttonContent?: ReactNode;
  extraMenuItems?: ExtraMenuItem[];
  classes?: Partial<Record<Parts, string>>;
  variant?: 'contained' | 'outlined';
  color?: 'primary' | 'secondary' | 'error' | 'inherit';
  triggerMode?: TriggerMode;
  trigger?: ReactElement;
};

export default function ImageUploadMenu({
  onUpload,
  allowedFileTypes,
  isUploading = false,
  scanProps,
  label,
  multiple = false,
  buttonContent,
  extraMenuItems = [],
  classes,
  variant = 'contained',
  color = 'primary',
  triggerMode = 'split',
  trigger,
}: Props) {
  const mixpanelTrack = useMixpanelTrack();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const trackEventNameSuffix = upperFirst(camelCase(scanProps?.captureType ?? ''));

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const menuItems: DropdownMenuItem[] = [
    {
      id: 'browse-files',
      label: 'Browse Files',
      onSelect: openFilePicker,
    },
    {
      id: 'capture-webcam',
      label: 'Capture from Webcam',
      onSelect: () => {
        mixpanelTrack(`onStartWebcamScan${trackEventNameSuffix}`);
        mediaEventChannel.emit('onStartWebcamScan', scanProps ?? {});
      },
    },
    ...extraMenuItems.map((item) => ({
      id: item.id,
      label: item.label,
      onSelect: item.onClick,
    })),
  ];

  const primaryLabel = buttonContent ?? (isUploading ? 'Uploading...' : label ?? 'Upload');
  const primaryStartIcon = buttonContent || isUploading
    ? undefined
    : <UploadIcon className="!w-4 !h-4" />;

  return (
    <>
      {triggerMode === 'menu' ? (
        <ActionMenu
          trigger={trigger ?? (
            <Button
              variant={variant}
              color={color}
              type="button"
              className={clsx(classes?.root, classes?.trigger)}
              disabled={isUploading}
            >
              {primaryLabel}
            </Button>
          )}
          items={menuItems}
          openOnHover={false}
          placement="bottom-end"
          paperClassName={classes?.popup}
          showArrow={false}
        />
      ) : (
        <SplitActionButton
          label={primaryLabel}
          startIcon={primaryStartIcon}
          onPrimaryClick={openFilePicker}
          items={menuItems}
          variant={variant}
          color={color}
          loading={isUploading}
          caretDirection="up"
          placement="top-end"
          paperClassName={classes?.popup}
          showArrow={false}
          classes={{
            root: classes?.root,
            primary: classes?.primary,
            caret: classes?.caret,
          }}
        />
      )}
      <FileInput
        ref={fileInputRef}
        onFileUpload={onUpload}
        allowedFileTypes={allowedFileTypes}
        multiple={multiple}
      />
    </>
  );
}
